import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getThumbBySize } from '../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import CustomControl from '../../../../../components/global/CustomControl';
import { IMAGE_BLUR_TYPES } from '../../../../../constants/image-blur-types';
import WatermarkOverlay from '../../../../../components/global/WatermarkOverlay';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
      }),
    ),
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  className: PropTypes.string,
  isEntitled: PropTypes.bool,
  isSingleGrid: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const CardPhotoSelect = ({
  photo,
  onSelect,
  selected,
  className,
  // Less disruptive. Better to fine-grain where we show the preview label.
  isEntitled = true,
  isSingleGrid,
  watermarkSettings,
}) => {
  const foregroundThumbnails = useMemo(() => {
    if (!photo?.foreground) {
      return [];
    }

    if (
      !isEntitled &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls;
    }

    if (photo?.foreground?.thumbnail_urls?.length > 0) {
      return photo?.foreground?.thumbnail_urls;
    }

    return [photo?.foreground?.original_url];
  }, [photo, isEntitled]);

  const thumbnail = useMemo(() => {
    if (foregroundThumbnails.length > 0) {
      return foregroundThumbnails[0].url;
    }

    if (!isEntitled && photo.faceblocked_thumbnail_urls?.length > 0) {
      return photo.faceblocked_thumbnail_urls?.filter(
        thumb => thumb.blur_type === IMAGE_BLUR_TYPES.FOREGROUND,
      )?.[0]?.url;
    }

    return getThumbBySize(photo.thumbnail_urls, IMAGE_SIZE_CODE.MEDIUM)?.url;
  }, [photo, isEntitled]);

  const classes = classNames({
    'card-photo-select': true,
    'is-selected': selected,
    [className]: !!className,
  });

  return (
    <a
      className={classes}
      href="#"
      onClick={event => {
        event.preventDefault();
        onSelect(photo);
      }}
    >
      <CustomControl
        id={photo.id}
        label="Photo Selected"
        labelSrOnly
        type="checkbox"
        checked={selected}
        size={isSingleGrid ? 'lg' : 'md'}
        readOnly
      />

      <div className="custom-print-config-photo-preview">
        <img className="custom-print-config-photo-img" src={thumbnail} alt="" />
        {!isEntitled && !photo?.foreground && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <WatermarkOverlay
              text={watermarkSettings?.wallpaperCustomText}
              fontSize={watermarkSettings?.wallpaperFontSize}
              wallpaperMode={watermarkSettings?.wallpaperWatermarkMode}
              imageUrl={watermarkSettings?.wallpaperAssetImage?.url}
            />
          </div>
        )}
      </div>
    </a>
  );
};

CardPhotoSelect.propTypes = propTypes;

export default CardPhotoSelect;
